import React, { useEffect, useState } from 'react';
import { attendanceService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

export default function ManagerPendingRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [form, setForm] = useState({ check_in_time: '', check_out_time: '', review_notes: '' });
  const [actionLoading, setActionLoading] = useState({});

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await attendanceService.getManagerPendingRequests();
      if (res && res.success) setRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch pending', err);
      alert(err.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const openApproveModal = (req) => {
    setActiveRequest(req);
    setForm({
      check_in_time: req.check_in_time || '',
      check_out_time: req.check_out_time || '',
      review_notes: ''
    });
    setModalOpen(true);
  };

  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    if (!form.check_in_time || !form.check_out_time) {
      alert('Please provide valid check-in and check-out times.');
      return;
    }
    const id = activeRequest.id;
    setActionLoading((s) => ({ ...s, [id]: true }));
    try {
      const payload = {
        status: 'approved',
        review_notes: form.review_notes || 'Approved by manager',
        check_in_time: form.check_in_time,
        check_out_time: form.check_out_time
      };
      const res = await attendanceService.reviewAttendanceRequest(id, payload);
      if (res && res.success) {
        setRequests((prev) => prev.filter((r) => r.id !== id));
        setModalOpen(false);
        setActiveRequest(null);
      } else {
        alert(res.message || 'Approve failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Approve failed');
    } finally {
      setActionLoading((s) => ({ ...s, [id]: false }));
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this request?')) return;
    setActionLoading((s) => ({ ...s, [id]: true }));
    try {
      const res = await attendanceService.reviewAttendanceRequest(id, { status: 'rejected', review_notes: 'Rejected by manager' });
      if (res && res.success) {
        setRequests((prev) => prev.filter((r) => r.id !== id));
      } else {
        alert(res.message || 'Reject failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Reject failed');
    } finally {
      setActionLoading((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="container mt-3">
      <h3>Pending Attendance Requests</h3>
      <p className="text-muted">Manager: {user?.first_name} {user?.last_name}</p>

      {loading ? <div>Loading...</div> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                <th>#</th><th>Employee</th><th>Date</th><th>In</th><th>Out</th><th>Reason</th><th>Attachment</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && <tr><td colSpan={8} className="text-center">No pending requests</td></tr>}
              {requests.map((r, idx) => (
                <tr key={r.id}>
                  <td>{idx + 1}</td>
                  <td>{r.first_name} {r.last_name}</td>
                  <td>{r.request_date}</td>
                  <td>{r.check_in_time || '-'}</td>
                  <td>{r.check_out_time || '-'}</td>
                  <td style={{ maxWidth: 240 }}>{r.reason}</td>
                  <td>{r.attachment_path ? <a href={r.attachment_path} target="_blank" rel="noreferrer">View</a> : '-'}</td>
                  <td>
                    <button className="btn btn-sm btn-success me-2" disabled={actionLoading[r.id]} onClick={() => openApproveModal(r)}>
                      {actionLoading[r.id] ? '...' : 'Approve'}
                    </button>
                    <button className="btn btn-sm btn-danger" disabled={actionLoading[r.id]} onClick={() => handleReject(r.id)}>
                      {actionLoading[r.id] ? '...' : 'Reject'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve modal */}
      {modalOpen && activeRequest && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Approve Request - {activeRequest.first_name} {activeRequest.last_name} ({activeRequest.request_date})</h5>
              <button className="btn-close" onClick={() => setModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleApproveSubmit}>
              <div className="modal-body">
                <div className="mb-2">
                  <label>Check-in Time</label>
                  <input type="time" className="form-control" value={form.check_in_time} onChange={(e) => setForm({...form, check_in_time: e.target.value})} required />
                </div>
                <div className="mb-2">
                  <label>Check-out Time</label>
                  <input type="time" className="form-control" value={form.check_out_time} onChange={(e) => setForm({...form, check_out_time: e.target.value})} required />
                </div>
                <div className="mb-2">
                  <label>Notes (optional)</label>
                  <textarea className="form-control" value={form.review_notes} onChange={(e) => setForm({...form, review_notes: e.target.value})} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-success">Approve</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
